import { useMutation, useQueryClient } from 'react-query';

import type { ProceduralTimelineEventSuggestion } from '../../../../backend/src/matter/procedural-timeline/procedural-timeline-event-suggestion.entity';
import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

interface Params {
  matterId: string;
  suggestionId: string;
  /** ISO 8601 string; the date the event will take when the suggestion is accepted. */
  startDateTime: string;
}

/**
 * Override a pending suggestion's proposed start date before accepting it
 * (e.g. a date resolved in the timeline calculator). The card refetches and
 * shows the new date; accepting then applies it.
 */
export const useSetProceduralSuggestionDate = () => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  return useMutation<ProceduralTimelineEventSuggestion, Error, Params>(
    async ({ matterId, suggestionId, startDateTime }) => {
      const res = await fetch(
        `${APIBaseChronos}/api/matter/${matterId}/procedural-timeline/suggestions/${suggestionId}`,
        getFetchConfig({ method: FetchMethod.PATCH, data: { startDateTime } }),
      );
      if (!res.ok) {
        const detail = await res.text().catch(() => '');
        throw new Error(detail || 'Failed to set the date');
      }
      return res.json();
    },
    {
      onSuccess: (_, { matterId }) => {
        queryClient.invalidateQueries([QueryEntity.ProceduralTimelineSuggestions, { matterId }]);
        queryClient.invalidateQueries([QueryEntity.ProceduralTimelineEvents, { matterId }]);
      },
    },
  );
};
